import { SolutionScenario } from '@/types/scenarios/solution';
import { Users, BookOpen, CalendarCheck, Building2, UserPlus, MessageSquare } from 'lucide-react';

export const clubsAssociations: SolutionScenario = {
  meta: {
    id: 'clubs-associations',
    slug: 'clubs-associations',
    name: 'Clubs & Associations',
    tagline: 'Genies that answer members so your volunteers can run the club',
    description:
      "A genie that handles member enquiries, booking requests, and the same questions about policies, fees, and facilities every day — so your volunteers and management can focus on the work that actually grows the club, not the call sheet.",
    market: 'Clubs & Associations',
    subMarkets: [
      'Sports & Recreation Clubs',
      'Community & Social Clubs',
      'Professional & Industry Associations',
      'Member-Based Not-for-Profits',
      'Leagues & Governing Bodies',
    ],
    specificNeeds: [
      'Member enquiry handling',
      'Policies, charters & rules',
      'Event booking',
      'Facilities booking',
      'New member enquiries',
      'Feedback & general enquiries',
    ],
    commonRoles: ['Club Secretaries', 'Volunteers', 'Membership Officers', 'Facility Managers', 'Association Administrators'],
    marketSize: 'Tens of thousands of clubs and associations run by a shrinking volunteer base',
    isFeatured: true,
  },

  hero: {
    headline: 'Members helped, volunteers freed',
    headlineGradient: 'Members helped,',
    headlineRest: 'volunteers freed',
    subheadline:
      "A genie that answers member enquiries, takes event and facility bookings, and handles the same policy questions every day so your volunteers can run the club, not the call sheet.",
  },

  recommendedPlaybooks: [
    'interactive-faq',
    'appointment-scheduler',
    'lead-capture',
    'proactive-welcome',
  ],

  applications: [
    {
      id: 'member-enquiries',
      name: 'Member Enquiries',
      description:
        'Answer the questions members ask every day — fees, hours, membership status, renewals, who to contact — instantly and accurately, day or night.',
      department: 'Membership',
      icon: Users,
      playbookIds: ['interactive-faq', 'proactive-welcome'],
      benefits: [
        '24/7 answers to common member questions',
        'Membership, fees & renewal queries handled',
        'Consistent answers every single time',
        'Volunteers freed from repetitive calls',
      ],
    },
    {
      id: 'policies-charters-rules',
      name: 'Policies, Charters & Rules',
      description:
        'Members and officials get clear, on-the-record answers about the constitution, by-laws, codes of conduct, and compliance obligations — straight from your documents.',
      department: 'Governance',
      icon: BookOpen,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Answers grounded in your official documents',
        'Consistent interpretation of rules & by-laws',
        'Reduces governance load on the committee',
        'Helps meet rising compliance expectations',
      ],
    },
    {
      id: 'event-booking',
      name: 'Event Booking',
      description:
        'Take event registrations and enquiries around the clock — fixtures, functions, AGMs, competitions, and social nights — capturing every attendee detail.',
      department: 'Events',
      icon: CalendarCheck,
      playbookIds: ['appointment-scheduler', 'lead-capture'],
      benefits: [
        'Round-the-clock event registrations',
        'Attendee details captured automatically',
        'Fewer no-shows with confirmations',
        'No volunteer chained to the inbox',
      ],
    },
    {
      id: 'facilities-booking',
      name: 'Facilities Booking',
      description:
        'Handle requests to book grounds, courts, rooms, and equipment — checking availability and confirming details without a volunteer playing phone tag.',
      department: 'Operations',
      icon: Building2,
      playbookIds: ['appointment-scheduler', 'interactive-faq'],
      benefits: [
        'Facility availability answered instantly',
        'Booking requests captured & confirmed',
        'Hire conditions explained up front',
        'Double-bookings and back-and-forth reduced',
      ],
    },
    {
      id: 'new-member-enquiries',
      name: 'New Member Enquiries',
      description:
        'Welcome prospective members the moment they reach out, answer their joining questions, and capture their details so the membership officer can follow up warm.',
      department: 'Growth',
      icon: UserPlus,
      playbookIds: ['lead-capture', 'proactive-welcome'],
      benefits: [
        'Every joining enquiry captured 24/7',
        'Membership tiers & benefits explained',
        'Prospect details handed to your team',
        'A welcoming first impression, every time',
      ],
    },
    {
      id: 'feedback-enquiries',
      name: 'Feedback & General Enquiries',
      description:
        'Give members an always-available place to raise feedback, ask general questions, and be heard — with everything logged and routed to the right person.',
      department: 'Member Experience',
      icon: MessageSquare,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        'Feedback captured and never lost',
        'General enquiries answered or routed',
        'Sentiment surfaced to the committee',
        'Members feel heard, around the clock',
      ],
    },
  ],

  challenges: [
    {
      title: 'A Shrinking Volunteer Base',
      description:
        "Volunteer numbers have roughly halved since 2018. The same handful of people are now answering every call, email, and enquiry on top of actually running the club.",
      impact: 'high',
      affectedAreas: ['Volunteer burnout', 'Member service', 'Club sustainability'],
    },
    {
      title: 'Rising Member Demands',
      description:
        'Members expect instant, always-on answers like they get everywhere else — but a volunteer-run club simply can\'t staff a phone line evenings and weekends.',
      impact: 'high',
      affectedAreas: ['Member satisfaction', 'Retention', 'Reputation'],
    },
    {
      title: 'Growing Compliance Requirements',
      description:
        'Governance, safety, and regulatory obligations keep increasing, and members and officials need consistent, on-the-record answers about policies, charters, and rules.',
      impact: 'high',
      affectedAreas: ['Governance', 'Risk', 'Committee workload'],
    },
    {
      title: 'After-Hours & Peak Coverage Gaps',
      description:
        'Enquiries spike before events, at season sign-on, and after hours — exactly when there\'s no one available to pick up the phone or clear the inbox.',
      impact: 'high',
      affectedAreas: ['Missed enquiries', 'Lost members', 'Event attendance'],
    },
  ],

  solutions: [
    {
      challenge: 'Too few volunteers handling too many repetitive enquiries',
      solution: 'A club Help Genie that answers member questions across phone, web, email, and an on-site QR code',
      implementation: 'Point your Genie at your existing rules, fees, and FAQs and let it field the routine enquiries automatically',
      expectedResults: [
        'Volunteers freed from the repetitive call load',
        'Members get instant answers, 24/7',
        'Committee time redirected to growing the club',
      ],
    },
    {
      challenge: 'No coverage after hours or during peak sign-on and events',
      solution: 'Always-on coverage that captures every enquiry, booking, and joining request, day or night',
      implementation: 'Deploy as your front line on the club phone, website, and inbox, escalating to the right person when needed',
      expectedResults: [
        'Zero missed enquiries after hours',
        'Event and facility bookings captured around the clock',
        'New member enquiries never fall through the cracks',
      ],
    },
    {
      challenge: 'Inconsistent answers on policies, charters, and compliance',
      solution: 'Answers grounded in your own official documents so members and officials hear the same thing every time',
      implementation: 'Upload your constitution, by-laws, codes of conduct, and policies to the knowledge base',
      expectedResults: [
        'Consistent, on-the-record policy answers',
        'Less governance load on the committee',
        'Stronger position on rising compliance demands',
      ],
    },
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'TidyHQ',
        category: 'Club Admin',
        description: 'Membership, events, and admin platform for clubs and associations',
        required: false,
        popularityScore: 88,
      },
      {
        name: 'GameDay (SportsTG)',
        category: 'Sports Management',
        description: 'Membership and competition management for sporting clubs',
        required: false,
        popularityScore: 85,
      },
      {
        name: 'MyClubMate',
        category: 'Membership',
        description: 'Membership and finance management for clubs',
        required: false,
        popularityScore: 80,
      },
      {
        name: 'Eventbrite',
        category: 'Events',
        description: 'Event registration and ticketing',
        required: false,
        popularityScore: 90,
      },
    ],
    common: [
      {
        name: 'Google Calendar',
        category: 'Scheduling',
        description: 'Calendar integration for events and facility bookings',
        required: false,
        popularityScore: 95,
      },
      {
        name: 'Mailchimp',
        category: 'Email',
        description: 'Member communications and newsletters',
        required: false,
        popularityScore: 88,
      },
    ],
  },

  metrics: {
    averageImplementationTime: 'About an hour',
    typicalROI: 'Hundreds of volunteer hours returned',
    adoptionRate: 'Members self-serve the routine questions',
    customerSatisfaction: '4.8/5',
  },

  demoConfig: {
    firstMessage:
      "Hi there! I'm your club's assistant. I can help with membership questions, event and facility bookings, club rules and policies, or pass you to the right person. How can I help today?",
    systemPrompt:
      "You are a warm, helpful assistant for a community club or association run largely by volunteers. Help members and prospective members with enquiries about membership, fees, opening hours, events, facility bookings, and club policies and rules. Answer from the club's documented information, capture contact details naturally, and offer to pass things to a committee member or volunteer when something needs a human. Be friendly, patient, and community-minded — you represent the club.",
    marketContext: 'Volunteer-run club or member-based association providing member services',
    exampleScenarios: [
      'How much is a family membership and how do I renew?',
      "I'd like to book the function room for a birthday next month",
      'What does the code of conduct say about junior players?',
      "I'm interested in joining — what's involved?",
    ],
  },

  caseStudies: [
    {
      companyName: 'Riverside Community & Sports Club',
      companySize: '1,400 members, 9 volunteers',
      location: 'Regional Australia',
      challenge:
        'A volunteer committee that had halved in size was fielding hundreds of calls and emails a month about fees, bookings, and rules — leaving no time for events, grants, or growth.',
      implementation:
        'Set up a club Help Genie in an afternoon, pointed it at the membership FAQ, by-laws, and booking rules, and put a QR code at the front door, on the website, and in the email signature.',
      timeline: 'Live the same day',
      results: [
        { metric: 'Routine Enquiries Handled', value: '70%+', improvement: 'Answered without a volunteer' },
        { metric: 'Volunteer Hours Returned', value: '20+ hrs/wk', improvement: 'Back to the committee' },
        { metric: 'After-Hours Enquiries', value: 'Captured', improvement: 'From missed to handled' },
        { metric: 'Booking Requests', value: '24/7', improvement: 'Never miss one' },
      ],
      quote: {
        text: "We're a handful of volunteers running a club for over a thousand members. The Genie answers the questions we were answering ten times a day, takes the booking enquiries while we're at work, and points people to the right rule. It gave us our evenings back.",
        author: 'Megan Doyle',
        title: 'Club Secretary',
      },
    },
  ],

  resources: [
    {
      type: 'guide',
      title: 'The Volunteer-Run Club Playbook',
      description: 'How clubs and associations are using a Genie to handle member enquiries without adding staff',
      url: '/resources/clubs-volunteer-playbook',
    },
    {
      type: 'webinar',
      title: 'Free Up Your Volunteers',
      description: 'See how to stand up a club Help Genie in about an hour and take the call load off your committee',
      url: '/webinars/clubs-free-up-volunteers',
    },
    {
      type: 'template',
      title: 'Club Member FAQ Templates',
      description: 'Ready-to-use answer sets for membership, bookings, events, and club rules',
      url: '/templates/clubs-faq',
    },
  ],

  faqs: [
    {
      question: 'Will this replace our volunteers?',
      answer:
        "No — it's there to support them, not replace them. The Genie takes the repetitive enquiries and after-hours load off your committee so your volunteers and management can focus on the higher-value work: events, growth, grants, and community. Anything that needs a human is passed straight to the right person.",
    },
    {
      question: 'How long does it take to set up?',
      answer:
        'Most clubs are up and running in about an hour. You point your Genie at the information you already have — your membership FAQ, fees, by-laws, and booking rules — and it\'s ready to answer. There\'s no heavy lift or technical project required from your club.',
    },
    {
      question: 'How will members actually reach the Genie?',
      answer:
        'Through the channels they already use. Your club Help Genie can answer on the phone, on your website, by email, and through an on-site QR code that members scan at the clubhouse, on a noticeboard, or on a flyer. It\'s one assistant, available everywhere your members are.',
    },
    {
      question: 'Can it answer questions about our specific rules and policies?',
      answer:
        'Yes. Upload your constitution, by-laws, codes of conduct, and policies, and the Genie answers members and officials from your own documents — so everyone gets the same consistent, on-the-record answer.',
    },
  ],

  pageContent: {
    scenariosHeader: {
      title: "Every member question handled\nso your volunteers don't have to",
      subtitle:
        "See how your club Help Genie handles the enquiries that fill your committee's evenings — membership, bookings, rules, and new members — across phone, web, email, and an on-site QR code.",
    },
    chatScenarios: [
      {
        id: 'member_enquiry',
        title: 'Member Enquiry',
        messages: [
          { type: 'user' as const, text: "Hi, I think my membership is due for renewal. How much is it for a family this year, and how do I pay?", delay: 300 },
          { type: 'genie' as const, text: "Happy to help! Family membership for this season is $260, which covers two adults and up to three juniors. You can renew online through the member portal, at the office on club days, or I can email you a secure payment link right now. Which would you prefer?", delay: 1200 },
          { type: 'user' as const, text: "A link would be great. Does that include the pool and the gym?", delay: 2500 },
          { type: 'genie' as const, text: "It does — family membership includes pool and gym access for everyone on the membership, plus discounted function room hire. I'll send the renewal link to the email on your account. Can I confirm the best email for you?", delay: 3400 },
          { type: 'user' as const, text: "Yes, it's megan.doyle@email.com.", delay: 4600 },
          { type: 'genie' as const, text: "Perfect — the renewal link is on its way to megan.doyle@email.com, and your family membership will roll over as soon as it's paid. Anything else I can help with while I'm here?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Membership fee answered instantly', delay: 6500 },
          { id: '2', text: 'Inclusions explained', delay: 6700 },
          { id: '3', text: 'Renewal payment link sent', delay: 6900 },
          { id: '4', text: 'Member contact confirmed', delay: 7100 },
          { id: '5', text: 'Zero volunteer time used', delay: 7300 },
        ],
      },
      {
        id: 'facility_booking',
        title: 'Facility Booking',
        messages: [
          { type: 'user' as const, text: "I'd like to book the function room for a 40th birthday on Saturday the 18th. Is it free?", delay: 300 },
          { type: 'genie' as const, text: "Let me check — yes, the function room is available on Saturday the 18th, both afternoon and evening sessions. For a 40th, the evening session (6pm–11pm) is the most popular. It seats up to 80 and includes the bar and kitchen. Would you like me to pencil that in?", delay: 1200 },
          { type: 'user' as const, text: "Evening would be perfect. What's the hire fee and do I need a bond?", delay: 2500 },
          { type: 'genie' as const, text: "For members, the evening hire is $350 plus a refundable $200 bond, and the bar is run by club staff. I'll log a booking request for Saturday the 18th, 6–11pm, and our facilities volunteer will confirm and send the hire agreement. Can I grab your name and the best contact number?", delay: 3400 },
          { type: 'user' as const, text: "Sure — James Whitfield, 0412 555 192.", delay: 4600 },
          { type: 'genie' as const, text: "Thanks James — I've logged your function room request for Saturday the 18th, 6–11pm, and flagged it for the facilities team to confirm. You'll get the hire agreement by email. Consider it pencilled in!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Availability checked instantly', delay: 6500 },
          { id: '2', text: 'Hire fee & bond explained', delay: 6700 },
          { id: '3', text: 'Booking request logged', delay: 6900 },
          { id: '4', text: 'Flagged to facilities volunteer', delay: 7100 },
          { id: '5', text: 'Contact details captured', delay: 7300 },
        ],
      },
      {
        id: 'policy_question',
        title: 'Rules & Policy',
        messages: [
          { type: 'user' as const, text: "My daughter wants to play in the under-12s but she's only 9. Do the rules allow her to play up an age group?", delay: 300 },
          { type: 'genie' as const, text: "Great question. Under our junior policy, a player can be approved to 'play up' one age group with the consent of a parent or guardian and sign-off from the junior coordinator, provided it's in the player's interest and safe to do so. Playing two or more groups up isn't permitted under the policy.", delay: 1200 },
          { type: 'user' as const, text: "Okay, so under-11s would be fine but under-12s is a stretch?", delay: 2500 },
          { type: 'genie' as const, text: "That's right — at 9, the under-11s would be within the one-group-up rule, while the under-12s would be two groups up and wouldn't be permitted. The junior coordinator makes the final call. Would you like me to pass your details on so they can talk it through with you?", delay: 3400 },
          { type: 'user' as const, text: "Yes please, that would be helpful.", delay: 4600 },
          { type: 'genie' as const, text: "Done — I've sent a note to the junior coordinator with your question, and they'll be in touch. Everything I shared comes straight from the club's junior participation policy, so it's the official position.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Answer grounded in club policy', delay: 6500 },
          { id: '2', text: 'Consistent, on-the-record reply', delay: 6700 },
          { id: '3', text: 'Edge case clarified correctly', delay: 6900 },
          { id: '4', text: 'Routed to junior coordinator', delay: 7100 },
          { id: '5', text: 'Committee time saved', delay: 7300 },
        ],
      },
      {
        id: 'new_member',
        title: 'New Member',
        messages: [
          { type: 'user' as const, text: "Hi, I just moved to the area and I'm interested in joining. What's involved?", delay: 300 },
          { type: 'genie' as const, text: "Welcome to the area, and great to hear you're interested! Joining is easy. We have social, full, and family memberships, and new members are welcome year-round. Are you joining mainly for the social side, to play, or for the facilities? That'll help me point you to the right membership.", delay: 1200 },
          { type: 'user' as const, text: "Mostly to play tennis and use the gym.", delay: 2500 },
          { type: 'genie' as const, text: "Then a full membership is the one — it includes competition tennis, full gym access, and member rates on coaching and court hire. It's $320 a year, or $30 a month. I can have our membership officer send you a welcome pack and get you set up. What's your name and email?", delay: 3400 },
          { type: 'user' as const, text: "Priya Nair, priya.nair@email.com.", delay: 4600 },
          { type: 'genie' as const, text: "Lovely, Priya — I've passed your details to our membership officer and flagged you as a prospective full member interested in tennis and the gym. You'll get a welcome pack shortly. We're really glad you found us!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Prospect welcomed warmly 24/7', delay: 6500 },
          { id: '2', text: 'Right membership recommended', delay: 6700 },
          { id: '3', text: 'Pricing explained clearly', delay: 6900 },
          { id: '4', text: 'Details sent to membership officer', delay: 7100 },
          { id: '5', text: 'Warm follow-up teed up', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_feedback',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "It's late, I know — but the lights on court 3 weren't working tonight and a few of us nearly rolled an ankle. Wanted to flag it.", delay: 300 },
          { type: 'genie' as const, text: "Thank you for flagging it, and I'm glad everyone's okay — that's exactly the kind of thing we want to know about straight away. I'm logging a facilities and safety report for court 3 lighting now, marked as a potential hazard. Roughly what time did you notice the lights failing?", delay: 1200 },
          { type: 'user' as const, text: "Around 8:30pm. Half the court was basically dark.", delay: 2500 },
          { type: 'genie' as const, text: "Got it — court 3 lighting failing from ~8:30pm, half the court in darkness, near-miss reported. I've sent this to the facilities manager as a priority and noted it for the safety officer so it's on the record. Would you like a text once it's been looked at?", delay: 3400 },
          { type: 'user' as const, text: "Yes please. Cheers for sorting it so quickly.", delay: 4600 },
          { type: 'genie' as const, text: "No worries at all — you'll get a text when it's actioned. Thanks for looking out for the other members. Reports like this help us keep everyone safe and stay on top of our obligations.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'After-hours hazard captured (8:30pm)', delay: 6500 },
          { id: '2', text: 'Safety report logged on the record', delay: 6700 },
          { id: '3', text: 'Escalated to facilities manager', delay: 6900 },
          { id: '4', text: 'Flagged to safety officer', delay: 7100 },
          { id: '5', text: 'Member updated by text', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Give Your Volunteers Their Time Back",
      subtitle:
        "The same questions, the same bookings, the same after-hours calls — every week. Your club Help Genie handles the routine so your committee can focus on the work that actually grows the club.",
    },
    useCaseCards: [
      {
        id: 'member-enquiries',
        title: 'Member Enquiries',
        description:
          "Fees, hours, renewals, who to contact — the questions your volunteers answer ten times a day. Your Genie answers them instantly, consistently, across phone, web, email, and a QR code.",
        bullets: [
          'Answers common member questions 24/7',
          'Membership, fees & renewal queries handled',
          'The same accurate answer every time',
          'Volunteers freed from the call load',
        ],
        image: '/solutions-pages/clubs-associations/usecase-member-enquiries.webp',
      },
      {
        id: 'bookings',
        title: 'Event & Facility Bookings',
        description:
          "Function room, courts, grounds, equipment, and event registrations — captured around the clock without a volunteer chained to the phone or inbox.",
        bullets: [
          'Availability answered instantly',
          'Booking requests captured & confirmed',
          'Hire conditions explained up front',
          'Fewer no-shows and double-bookings',
        ],
        image: '/solutions-pages/clubs-associations/usecase-bookings.webp',
      },
      {
        id: 'new-members',
        title: 'New Member Sign-Ups',
        description:
          "Prospective members get a warm welcome the moment they reach out, the right membership recommended, and their details handed to your membership officer for a warm follow-up.",
        bullets: [
          'Every joining enquiry captured 24/7',
          'Right membership tier recommended',
          'Prospect details sent to your team',
          'A welcoming first impression, every time',
        ],
        image: '/solutions-pages/clubs-associations/usecase-new-members.webp',
      },
      {
        id: 'policies',
        title: 'Policies, Rules & Compliance',
        description:
          "Members and officials get clear, consistent answers about your constitution, by-laws, and codes of conduct — straight from your own documents — easing the committee's governance load.",
        bullets: [
          'Answers grounded in your official documents',
          'Consistent interpretation of the rules',
          'Reduces governance load on the committee',
          'Helps meet rising compliance demands',
        ],
        image: '/solutions-pages/clubs-associations/usecase-policies.webp',
      },
    ],
    finalCTA: {
      title: "Run the Club, Not the Call Sheet",
      subtitle:
        "Volunteer numbers have halved, member demands keep rising, and compliance keeps growing. Your club Help Genie absorbs the enquiries, bookings, and policy questions — across every channel — so your people can focus on what actually matters. Live in about an hour.",
      pillars: [
        {
          title: 'Always\nAnswering',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Member questions, fees, hours, and rules answered 24/7 across phone, web, email, and QR.',
        },
        {
          title: 'Always\nBooking',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Events, facilities, and new member enquiries captured around the clock — never missed.',
        },
        {
          title: 'Always\nSupporting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Built to free your volunteers, not replace them — handing the right things to the right people.',
        },
      ],
      backgroundImage: '/solutions-pages/clubs-associations/cta-background.webp',
    },
  },
};
